import React, { useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { FaDownload, FaLink, FaSpinner, FaTimes } from 'react-icons/fa';
import { useTheme } from '../themeConfig';
import { useFeedback } from '../context/FeedbackContext';

const CARD_WIDTH = 1080;
const CARD_HEIGHT = 1920;

const findFirstMedia = (modules = []) => {
  for (const module of modules) {
    const data = module?.data || {};
    if ((module.type === 'primary_media' || module.type === 'hero_media') && data.mediaType === 'photo') {
      const imageUrl = data.images?.find((item) => item?.url)?.url;
      if (imageUrl) return imageUrl;
    }
    if ((module.type === 'keepsake_poster' || module.type === 'media_card') && data.mediaType !== 'video' && data.mediaUrl) {
      return data.mediaUrl;
    }
  }
  return '';
};

const safeText = (value, fallback = '') => String(value || fallback).trim();

const loadImage = (url) => new Promise((resolve) => {
  if (!url) {
    resolve(null);
    return;
  }
  const image = new Image();
  const timer = setTimeout(() => resolve(null), 4500);
  image.crossOrigin = 'anonymous';
  image.onload = () => {
    clearTimeout(timer);
    resolve(image);
  };
  image.onerror = () => {
    clearTimeout(timer);
    resolve(null);
  };
  image.src = url;
});

const drawRoundRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, radius);
  ctx.closePath();
};

const drawWrappedText = (ctx, text, x, y, maxWidth, lineHeight, maxLines) => {
  const words = safeText(text).split(/\s+/).filter(Boolean);
  let line = '';
  let lines = 0;
  let currentY = y;

  for (let i = 0; i < words.length; i += 1) {
    const testLine = `${line}${words[i]} `;
    if (ctx.measureText(testLine).width > maxWidth && i > 0) {
      const suffix = lines === maxLines - 1 ? '...' : '';
      ctx.fillText(`${line.trim()}${suffix}`, x, currentY);
      line = `${words[i]} `;
      currentY += lineHeight;
      lines += 1;
      if (lines >= maxLines) return currentY;
    } else {
      line = testLine;
    }
  }

  if (line && lines < maxLines) {
    ctx.fillText(line.trim(), x, currentY);
    currentY += lineHeight;
  }
  return currentY;
};

const drawImageCover = (ctx, image, x, y, width, height, radius) => {
  ctx.save();
  drawRoundRect(ctx, x, y, width, height, radius);
  ctx.clip();
  const scale = Math.max(width / image.width, height / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  ctx.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
  ctx.restore();
};

const downloadCanvas = (canvas, recipient) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('The share card image could not be generated.'));
      return;
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lumirae-${recipient.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-share-card.png`;
    link.click();
    URL.revokeObjectURL(url);
    resolve();
  }, 'image/png', 1);
});

const ShareCardModal = ({
  isOpen,
  onClose,
  pageData = {},
  storyAnalysis = {},
}) => {
  const { theme } = useTheme();
  const feedback = useFeedback();
  const qrCanvasRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState('');
  const [saveNotice, setSaveNotice] = useState('');
  const [saveError, setSaveError] = useState('');

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const imageUrl = useMemo(() => findFirstMedia(pageData.modules), [pageData.modules]);
  const recipient = safeText(pageData.ownerName, 'Someone Special');
  const headline = safeText(pageData.openingTitle, `A gift for ${recipient}`);
  const finalLine = safeText(
    pageData.storyCoachAnswers?.afterglow ||
    pageData.storyCoachAnswers?.peak ||
    pageData.openingSubtitle,
    'A Lumirae keepsake made to be opened slowly.'
  );
  const modeLabel = pageData.sharingSettings?.shareMode === 'private'
    ? 'Sharing Off'
    : pageData.sharingSettings?.shareMode === 'moments'
      ? 'Moment Share'
      : 'Link Story';

  const saveCard = async () => {
    setIsSaving(true);
    setSaveError('');
    setSaveNotice('');
    try {
      const canvas = document.createElement('canvas');
      canvas.width = CARD_WIDTH;
      canvas.height = CARD_HEIGHT;
      const ctx = canvas.getContext('2d');
      const mediaImage = await loadImage(imageUrl);

      const gradient = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
      gradient.addColorStop(0, theme.bg || '#fdf2f8');
      gradient.addColorStop(0.52, theme.accentSoft || theme.inputBg || '#fce7f3');
      gradient.addColorStop(1, '#ffffff');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

      ctx.fillStyle = 'rgba(255,255,255,0.62)';
      ctx.beginPath();
      ctx.arc(880, 280, 250, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = theme.accent || '#f472b6';
      ctx.globalAlpha = 0.16;
      ctx.beginPath();
      ctx.arc(140, 260, 280, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;

      ctx.fillStyle = 'rgba(255,255,255,0.78)';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 10;
      drawRoundRect(ctx, 90, 90, 900, 1740, 72);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = theme.text || '#211B1D';
      ctx.font = '900 28px Arial';
      ctx.fillText(modeLabel.toUpperCase(), 150, 190);
      ctx.globalAlpha = 0.45;
      ctx.fillText('LUMIRAE', 790, 190);
      ctx.globalAlpha = 1;

      ctx.font = '900 30px Arial';
      ctx.globalAlpha = 0.45;
      ctx.fillText('FOR', 150, 330);
      ctx.globalAlpha = 1;
      ctx.font = 'italic 900 92px Arial';
      drawWrappedText(ctx, recipient.toUpperCase(), 150, 440, 780, 92, 3);

      ctx.fillStyle = 'rgba(255,255,255,0.86)';
      drawRoundRect(ctx, 150, 690, 780, 470, 64);
      ctx.fill();
      if (mediaImage) {
        drawImageCover(ctx, mediaImage, 180, 720, 720, 410, 48);
      } else {
        ctx.fillStyle = theme.text || '#211B1D';
        ctx.font = 'italic 900 66px Arial';
        drawWrappedText(ctx, 'OPEN SLOWLY', 260, 940, 560, 72, 2);
      }

      ctx.fillStyle = 'rgba(255,255,255,0.76)';
      drawRoundRect(ctx, 150, 1225, 780, 330, 56);
      ctx.fill();
      ctx.fillStyle = theme.text || '#211B1D';
      ctx.font = '900 48px Arial';
      drawWrappedText(ctx, headline.toUpperCase(), 200, 1320, 680, 56, 3);
      ctx.globalAlpha = 0.72;
      ctx.font = 'italic 700 34px Arial';
      drawWrappedText(ctx, finalLine.slice(0, 150), 200, 1485, 680, 42, 3);
      ctx.globalAlpha = 1;

      const qrCanvas = qrCanvasRef.current?.querySelector('canvas');
      if (qrCanvas) {
        ctx.fillStyle = '#ffffff';
        drawRoundRect(ctx, 740, 1620, 190, 190, 34);
        ctx.fill();
        ctx.drawImage(qrCanvas, 765, 1645, 140, 140);
      }

      ctx.font = '900 26px Arial';
      ctx.globalAlpha = 0.5;
      ctx.fillText(`${storyAnalysis.momentCount || pageData.modules?.length || 0} MOMENTS`, 150, 1705);
      ctx.fillText('MADE TO KEEP', 150, 1760);
      ctx.globalAlpha = 1;

      const imageDataUrl = canvas.toDataURL('image/png');
      setGeneratedImageUrl(imageDataUrl);

      try {
        await downloadCanvas(canvas, recipient);
        setSaveNotice('If the download did not appear, long-press the preview below and save the image.');
        feedback.toast({
          type: 'success',
          title: 'Share Card Ready',
          message: 'The keepsake card is ready to save or share.',
        });
      } catch (downloadError) {
        setSaveError(downloadError.message || 'Download was blocked by this browser.');
        setSaveNotice('Long-press the preview below and choose Save Image, or copy the gift link instead.');
      }
    } catch (error) {
      setSaveError(error.message || 'Share card could not be generated.');
      setSaveNotice('Try copying the gift link instead.');
    } finally {
      setIsSaving(false);
    }
  };

  const copyLink = async () => {
    await feedback.copyText({
      title: 'Gift Link',
      message: 'Copy this gift link.',
      value: shareUrl,
      successMessage: 'Gift link copied.',
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] overflow-y-auto bg-black/60 p-4 backdrop-blur-md"
          data-html2canvas-ignore="true"
          onMouseDown={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
        >
          <div ref={qrCanvasRef} className="pointer-events-none fixed -left-[9999px] -top-[9999px]">
            <QRCodeCanvas value={shareUrl || 'https://thelumirae.com'} size={256} level="H" includeMargin />
          </div>

          <motion.div
            initial={{ scale: 0.95, y: 16 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 16 }}
            className="mx-auto my-4 w-full max-w-md"
            style={{
              '--lumirae-accent-primary': theme.accent,
              '--lumirae-accent-soft': theme.accentSoft,
              '--lumirae-surface-raised': theme.cardBg,
              '--lumirae-surface-base': theme.bg,
              '--lumirae-border-default': theme.cardBorder,
              '--lumirae-text-primary': theme.text,
              '--lumirae-text-secondary': theme.secondaryText,
            }}
          >
            <div className="lumirae-ds-panel mb-3 flex items-center justify-between px-4 py-3">
              <div>
                <div className="lumirae-ds-meta" style={{ color: theme.accent }}>
                  Share Card
                </div>
                <div className="text-xs font-black uppercase tracking-[0.12em]" style={{ color: theme.text }}>
                  1080 x 1920 story card
                </div>
              </div>
              <button
                onClick={onClose}
                className="lumirae-ds-icon-button"
                aria-label="Close share card"
              >
                <FaTimes />
              </button>
            </div>

            <div
              className="relative mx-auto h-[640px] w-[360px] max-w-full overflow-hidden rounded-[2rem] border p-6 shadow-2xl"
              style={{
                background: `linear-gradient(145deg, ${theme.bg}, ${theme.accentSoft || theme.inputBg}, #ffffff)`,
                borderColor: theme.cardBorder,
                color: theme.text,
              }}
            >
              <div className="absolute -left-16 top-10 h-40 w-40 rounded-full blur-3xl" style={{ backgroundColor: theme.accent, opacity: 0.22 }} />
              <div className="absolute -right-20 bottom-24 h-52 w-52 rounded-full bg-white/55 blur-3xl" />

              <div className="relative z-10 flex h-full flex-col">
                <div className="flex items-center justify-between">
                  <div className="rounded-full border px-4 py-2 text-[8px] font-black uppercase tracking-[0.25em]" style={{ borderColor: theme.cardBorder, backgroundColor: `${theme.cardBg}cc` }}>
                    {modeLabel}
                  </div>
                  <div className="text-[8px] font-black uppercase tracking-[0.28em] opacity-45">
                    Lumirae
                  </div>
                </div>

                <div className="mt-6">
                  <div className="text-[9px] font-black uppercase tracking-[0.35em] opacity-45">
                    For
                  </div>
                  <h2 className="mt-2 break-words text-3xl font-black uppercase italic leading-none">
                    {recipient}
                  </h2>
                </div>

                <div className="mt-5 overflow-hidden rounded-[2rem] border shadow-xl" style={{ borderColor: theme.cardBorder, backgroundColor: `${theme.cardBg}aa` }}>
                  {imageUrl ? (
                    <img src={imageUrl} alt="" className="h-40 w-full object-cover" crossOrigin="anonymous" />
                  ) : (
                    <div className="flex h-40 items-center justify-center px-8 text-center text-2xl font-black uppercase italic">
                      Open Slowly
                    </div>
                  )}
                </div>

                <div className="mt-5 rounded-[2rem] border p-4 shadow-inner" style={{ borderColor: theme.cardBorder, backgroundColor: `${theme.cardBg}bb` }}>
                  <h3 className="break-words text-lg font-black uppercase leading-tight">
                    {headline}
                  </h3>
                  <p className="mt-2 break-words text-xs font-bold italic leading-relaxed opacity-70">
                    {finalLine.slice(0, 150)}
                  </p>
                </div>

                <div className="mt-auto flex items-end justify-between gap-4">
                  <div>
                    <div className="text-[8px] font-black uppercase tracking-[0.25em] opacity-45">
                      {storyAnalysis.momentCount || pageData.modules?.length || 0} Moments
                    </div>
                    <div className="mt-1 text-[8px] font-black uppercase tracking-[0.25em] opacity-45">
                      Made to Keep
                    </div>
                  </div>
                  <div className="rounded-[1.25rem] border p-2 shadow-lg" style={{ borderColor: theme.cardBorder, backgroundColor: theme.cardBg }}>
                    <QRCodeSVG value={shareUrl || 'https://thelumirae.com'} size={70} level="H" />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                onClick={saveCard}
                disabled={isSaving}
                className="lumirae-ds-button lumirae-ds-button-secondary px-5 py-4 shadow-xl disabled:opacity-50"
              >
                {isSaving ? <FaSpinner className="mr-2 inline animate-spin" /> : <FaDownload className="mr-2 inline" />}
                Save Card
              </button>
              <button
                onClick={copyLink}
                className="lumirae-ds-button lumirae-ds-button-primary px-5 py-4 shadow-xl"
              >
                <FaLink className="mr-2 inline" />
                Copy Link
              </button>
            </div>

            {generatedImageUrl && (
              <div className="lumirae-ds-panel mt-4 space-y-4 p-4">
                <div>
                  <div className="lumirae-ds-meta" style={{ color: theme.accent }}>
                    Ready to Save
                  </div>
                  <h3 className="mt-1 text-sm font-black uppercase tracking-[0.12em]" style={{ color: theme.text }}>
                    Your share card is generated
                  </h3>
                  <p className="mt-2 text-xs font-bold leading-relaxed" style={{ color: theme.secondaryText }}>
                    {saveNotice || 'Save the image or copy the gift link from here.'}
                  </p>
                  {saveError && (
                    <p className="mt-2 rounded-2xl bg-red-50 px-3 py-2 text-xs font-black uppercase leading-relaxed tracking-[0.1em] text-red-500">
                      {saveError}
                    </p>
                  )}
                </div>

                <a
                  href={generatedImageUrl}
                  download={`lumirae-${recipient.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-share-card.png`}
                  className="block overflow-hidden rounded-[1.5rem] border bg-white p-2 shadow-xl"
                  style={{ borderColor: theme.cardBorder }}
                  aria-label="Open generated share card image"
                >
                  <img
                    src={generatedImageUrl}
                    alt="Generated Lumirae share card"
                    className="mx-auto max-h-[520px] w-full rounded-[1.1rem] object-contain"
                  />
                </a>

                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={generatedImageUrl}
                    download={`lumirae-${recipient.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-share-card.png`}
                    className="lumirae-ds-button lumirae-ds-button-secondary px-5 py-4 text-center shadow-xl"
                  >
                    <FaDownload className="mr-2 inline" />
                    Save Image
                  </a>
                  <button
                    type="button"
                    onClick={copyLink}
                    className="lumirae-ds-button lumirae-ds-button-primary px-5 py-4 shadow-xl"
                  >
                    <FaLink className="mr-2 inline" />
                    Copy Link
                  </button>
                </div>

                <p className="text-center text-[10px] font-black uppercase leading-relaxed tracking-[0.16em]" style={{ color: theme.secondaryText }}>
                  On iPhone or Android, long-press the image preview if browser download is blocked.
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShareCardModal;
